import { useState, useRef, useEffect } from 'react';

type NotificationVariant = 'success' | 'error';

export const useNotification = () => {
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationVariant, setNotificationVariant] = useState<NotificationVariant>('success');

  const displayNotification = (message: string, variant: NotificationVariant = 'success') => {
    setNotificationMessage(message);
    setNotificationVariant(variant);
    setShowNotification(true);
  };

  const hideNotification = () => setShowNotification(false);

  return {
    showNotification,
    notificationMessage,
    notificationVariant,
    displayNotification,
    hideNotification,
  };
};

/**
 * Enhanced notification hook with auto-close functionality
 * Useful for dialogs that need to close automatically after showing a success notification
 */
export const useNotificationWithAutoClose = () => {
  const [isVisibleNotification, setIsVisibleNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationVariant, setNotificationVariant] = useState<NotificationVariant>('success');
  const timerRef = useRef<number | null>(null);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  /**
   * Display a notification message with optional auto-close callback
   * @param message - Notification message to display
   * @param variant - Notification variant ('success' | 'error')
   * @param onAutoClose - Optional callback to execute after auto-close delay
   * @param autoCloseDelay - Delay in ms before executing onAutoClose (default: 3500ms)
   */
  const displayNotification = (
    message: string,
    variant: NotificationVariant = 'success',
    onAutoClose?: () => void,
    autoCloseDelay: number = 3500,
  ) => {
    setNotificationMessage(message);
    setNotificationVariant(variant);
    setIsVisibleNotification(true);

    if (onAutoClose) {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
      }

      timerRef.current = window.setTimeout(() => {
        setIsVisibleNotification(false);
        onAutoClose();
        timerRef.current = null;
      }, autoCloseDelay);
    }
  };

  const hideNotification = () => {
    setIsVisibleNotification(false);
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  return {
    isVisibleNotification,
    notificationMessage,
    notificationVariant,
    displayNotification,
    hideNotification,
  };
};
